import { Box, Button, IconButton, InputBase, Modal, TextField, Tooltip, Typography, Switch } from "@mui/material";
import SearchIcon from '@mui/icons-material/Search';
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState, useRef } from "react";
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Toast } from "primereact/toast";
import CustomAxios from "../../utils/CustomAxios";
import * as Yup from "yup";
import { 
    updateFranchises, 
    updateFranchiseForm, 
    updateFranchiseModal, 
    updateFranchiseEdit, 
    updateFranchiseLoading, 
    updateSelectedFranchiseId, 
    resetFranchiseForm 
} from "../../Redux/inventory";
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';

const validationSchema = Yup.object().shape({
    name: Yup.string().required('Franchise name is required'),
    description: Yup.string().required('Description is required'),
    contact_person: Yup.string().required('Contact person is required'),
    contact_email: Yup.string().email('Invalid email format').required('Contact email is required'),
    contact_phone: Yup.string().required('Contact phone is required'),
    address: Yup.string().required('Address is required'),
});

export function FranchiseManagement() {
    const toast = useRef(null);
    const dispatch = useDispatch();
    const franchises = useSelector(state => state.inventory.franchises);
    const franchiseForm = useSelector(state => state.inventory.franchiseForm);
    const franchiseModal = useSelector(state => state.inventory.franchiseModal);
    const franchiseEdit = useSelector(state => state.inventory.franchiseEdit);
    const franchiseLoading = useSelector(state => state.inventory.franchiseLoading);
    const selectedFranchiseId = useSelector(state => state.inventory.selectedFranchiseId);
    const [formData, setFormData] = useState(franchiseForm);
    const [errors, setErrors] = useState({});
    const [logoPreview, setLogoPreview] = useState('');
    const [uploadingLogo, setUploadingLogo] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        fetchFranchises();
    }, []);

    useEffect(() => {
        setFormData(franchiseForm);
    }, [franchiseForm]);

    const fetchFranchises = async () => {
        try {
            const response = await CustomAxios.get('/franchise/');
            dispatch(updateFranchises(response.data));
        } catch (error) {
            console.error('Error fetching franchises:', error);
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to fetch franchises',
                life: 3000,
            });
        }
    };

    const handleOpenModal = (edit = false, franchise = null) => {
        if (edit && franchise) {
            dispatch(updateFranchiseForm({
                name: franchise.name,
                description: franchise.description,
                contact_person: franchise.contact_person,
                contact_email: franchise.contact_email,
                contact_phone: franchise.contact_phone,
                address: franchise.address,
                is_active: franchise.is_active,
                logo_url: franchise.logo_url || '',
                background_color: franchise.background_color || '',
                content_color: franchise.content_color || '',
                border_color: franchise.border_color || ''
            }));
            dispatch(updateSelectedFranchiseId(franchise.id));
            dispatch(updateFranchiseEdit(true));
            setLogoPreview(franchise.logo_url || '');
        } else {
            dispatch(resetFranchiseForm());
            dispatch(updateSelectedFranchiseId(null));
            dispatch(updateFranchiseEdit(false));
            setLogoPreview('');
        }
        dispatch(updateFranchiseModal(true));
        setErrors({});
    };

    const handleCloseModal = () => {
        dispatch(updateFranchiseModal(false));
        dispatch(resetFranchiseForm());
        setErrors({});
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        const newValue = type === 'checkbox' ? checked : value;
        
        setFormData(prev => ({
            ...prev,
            [name]: newValue
        }));
        
        dispatch(updateFranchiseForm({
            ...formData,
            [name]: newValue
        }));

        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const uploadFranchiseLogo = async (file) => {
        try {
            setUploadingLogo(true);
            const form = new FormData();
            form.append('file', file);
            const res = await CustomAxios.post('/franchise/logo-upload/', form);
            const stableUrl = res?.data?.stable_url;
            if (!stableUrl) {
                throw new Error(res?.data?.error || 'Upload URL not returned');
            }

            setLogoPreview(stableUrl);
            const updated = { ...formData, logo_url: stableUrl };
            setFormData(updated);
            dispatch(updateFranchiseForm(updated));
            toast.current?.show?.({
                severity: 'success',
                summary: 'Success',
                detail: 'Logo uploaded successfully',
                life: 3000,
            });
        } catch (e) {
            console.error('Logo upload failed:', e);
            const detail = e?.response?.data?.error
                || e?.message
                || 'Logo upload failed';
            toast.current?.show?.({ severity: 'error', summary: 'Error', detail, life: 3000 });
        } finally {
            setUploadingLogo(false);
        }
    };

    const handleLogoFileChange = (e) => {
        const file = e.target.files && e.target.files[0];
        if (!file) return;

        const maxSize = 5 * 1024 * 1024;
        if (file.size > maxSize) {
            toast.current?.show?.({
                severity: 'error',
                summary: 'Error',
                detail: 'Logo file size should not exceed 5MB',
                life: 3000,
            });
            e.target.value = '';
            return;
        }

        const isValidType =
            file.type === 'image/jpeg' ||
            file.type === 'image/png' ||
            file.type === 'image/webp' ||
            file.type === 'image/gif' ||
            /\.(jpg|jpeg|png|webp|gif)$/i.test(file.name);
        if (!isValidType) {
            toast.current?.show?.({
                severity: 'error',
                summary: 'Error',
                detail: 'Logo must be jpeg, png, webp, or gif',
                life: 3000,
            });
            e.target.value = '';
            return;
        }

        uploadFranchiseLogo(file);
        e.target.value = '';
    };

    const handleRemoveLogo = () => {
        setLogoPreview('');
        const updated = { ...formData, logo_url: '' };
        setFormData(updated);
        dispatch(updateFranchiseForm(updated));
    };

    const handleSubmit = async () => {
        try {
            await validationSchema.validate(formData, { abortEarly: false });
            setErrors({});
            
            dispatch(updateFranchiseLoading(true));
            
            if (franchiseEdit) {
                // Update existing franchise
                if (!selectedFranchiseId && selectedFranchiseId !== 0) {
                    throw new Error('Franchise id is missing for update');
                }

                const existing = franchises.find(f => f.id === selectedFranchiseId);
                const updatePayload = { ...formData };
                if (existing && existing.name === formData.name) {
                    delete updatePayload.name;
                }

                await CustomAxios.put(`/franchise/update/${selectedFranchiseId}/`, updatePayload);
                toast.current.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Franchise updated successfully',
                    life: 3000,
                });
            } else {
                // Create new franchise
                await CustomAxios.post('/franchise/create/', formData);
                toast.current.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Franchise created successfully',
                    life: 3000,
                });
            }
            
            fetchFranchises();
            handleCloseModal();
            
        } catch (validationErrors) {
            if (validationErrors.name === 'ValidationError') {
                const formErrors = {};
                validationErrors.inner.forEach((error) => {
                    formErrors[error.path] = error.message;
                });
                setErrors(formErrors);
            } else {
                const backendMessage = validationErrors?.response?.data?.error
                    || validationErrors?.response?.data?.detail
                    || (typeof validationErrors?.response?.data === 'string' ? validationErrors.response.data : null)
                    || 'An error occurred while saving franchise';
                toast.current.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: backendMessage,
                    life: 3000,
                });
            }
        } finally {
            dispatch(updateFranchiseLoading(false));
        }
    };

    const handleDelete = (franchise) => {
        confirmDialog({
            message: `Are you sure you want to delete the franchise "${franchise.name}"?`,
            header: 'Delete Confirmation',
            icon: 'pi pi-exclamation-triangle',
            accept: async () => {
                try {
                    await CustomAxios.delete(`/franchise/delete/${franchise.id}/`);
                    toast.current.show({
                        severity: 'success',
                        summary: 'Success',
                        detail: 'Franchise deleted successfully',
                        life: 3000,
                    });
                    fetchFranchises();
                } catch (error) {
                    toast.current.show({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Failed to delete franchise',
                        life: 3000,
                    });
                }
            }
        });
    };

    const filteredFranchises = franchises.filter(franchise =>
        franchise.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        franchise.contact_person.toLowerCase().includes(searchTerm.toLowerCase()) ||
        franchise.contact_email.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <Box sx={{ padding: "24px", backgroundColor: "#F5F5F5", minHeight: "100vh" }}>
            <Toast ref={toast} />
            <ConfirmDialog />
            
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "24px" }}>
                <Typography sx={{ fontSize: "24px", fontWeight: "600", lineHeight: "32px" }}>
                    Franchise Management
                </Typography>
                <Button 
                    variant="contained" 
                    onClick={() => handleOpenModal(false)}
                    startIcon={<AddIcon />}
                    sx={{ textTransform: "none" }}
                >
                    Add Franchise
                </Button>
            </Box>

            <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%", marginBottom: "24px" }}>
                <Box sx={{ position: "relative" }}>
                    <InputBase 
                        sx={{ 
                            backgroundColor: "white", 
                            height: "32px", 
                            width: "100%", 
                            maxWidth: "268px", 
                            borderRadius: "2px", 
                            border: "1px solid", 
                            borderColor: "secondary.border", 
                            padding: "5px 12px 5px 40px", 
                            fontSize: "14px", 
                            fontWeight: "400", 
                            lineHeight: "22px", 
                            color: "black" 
                        }} 
                        onChange={(e) => setSearchTerm(e.target.value)} 
                        placeholder="Search franchises" 
                    />
                    <SearchIcon sx={{ position: "absolute", left: "12px", top: "5px", width: "20px", height: "20px" }} />
                </Box>
            </Box>

            <Box sx={{ backgroundColor: "white", borderRadius: "8px", padding: "24px" }}>
                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: "16px" }}>
                    {filteredFranchises.map((franchise) => (
                        <Box 
                            key={franchise.id} 
                            sx={{ 
                                border: "1px solid #E0E0E0", 
                                borderRadius: "8px", 
                                padding: "16px",
                                position: "relative"
                            }}
                        >
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "12px" }}>
                                <Typography sx={{ fontSize: "18px", fontWeight: "600", lineHeight: "24px" }}>
                                    {franchise.name}
                                </Typography>
                                <Box sx={{ display: "flex", gap: "8px" }}>
                                    <Tooltip title="Edit Franchise">
                                        <IconButton 
                                            size="small" 
                                            onClick={() => handleOpenModal(true, franchise)}
                                            sx={{ color: "#1976d2" }}
                                        >
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Delete Franchise">
                                        <IconButton 
                                            size="small" 
                                            onClick={() => handleDelete(franchise)}
                                            sx={{ color: "#d32f2f" }}
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            </Box>
                            
                            <Typography sx={{ fontSize: "14px", color: "#666", marginBottom: "8px" }}>
                                {franchise.description}
                            </Typography>
                            
                            <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                                <Typography sx={{ fontSize: "12px", color: "#666" }}>
                                    <strong>Contact:</strong> {franchise.contact_person}
                                </Typography>
                                <Typography sx={{ fontSize: "12px", color: "#666" }}>
                                    <strong>Email:</strong> {franchise.contact_email}
                                </Typography>
                                <Typography sx={{ fontSize: "12px", color: "#666" }}>
                                    <strong>Phone:</strong> {franchise.contact_phone}
                                </Typography>
                                <Typography sx={{ fontSize: "12px", color: "#666" }}>
                                    <strong>Address:</strong> {franchise.address}
                                </Typography>
                                <Typography sx={{ fontSize: "12px", color: "#666" }}>
                                    <strong>Status:</strong> 
                                    <span style={{ color: franchise.is_active ? "#4caf50" : "#f44336" }}>
                                        {franchise.is_active ? " Active" : " Inactive"}
                                    </span>
                                </Typography>
                            </Box>
                        </Box>
                    ))}
                </Box>
                
                {filteredFranchises.length === 0 && (
                    <Box sx={{ textAlign: "center", padding: "48px" }}>
                        <Typography sx={{ fontSize: "16px", color: "#666" }}>
                            {searchTerm ? "No franchises found matching your search." : "No franchises available."}
                        </Typography>
                    </Box>
                )}
            </Box>

            {/* Franchise Modal */}
            <Modal
                open={franchiseModal}
                onClose={handleCloseModal}
                aria-labelledby="franchise-modal-title"
                aria-describedby="franchise-modal-description"
                sx={{ zIndex: 1000 }}
            >
                <Box sx={{ 
                    position: 'absolute', 
                    top: '50%', 
                    left: '50%', 
                    transform: 'translate(-50%, -50%)', 
                    width: '100%', 
                    maxWidth: "600px", 
                    backgroundColor: 'white',
                    borderRadius: "8px",
                    boxShadow: 24
                }}>
                    <Box sx={{ 
                        height: "46px", 
                        padding: "12px 24px", 
                        backgroundColor: "#EEEEEE", 
                        display: "flex", 
                        alignItems: "center",
                        borderTopLeftRadius: "8px",
                        borderTopRightRadius: "8px"
                    }}>
                        <Typography sx={{ fontSize: "14px", fontWeight: "500", lineHeight: "22px" }}>
                            {franchiseEdit ? 'Edit Franchise' : 'Add Franchise'}
                        </Typography>
                    </Box>
                    
                    <Box sx={{ display: "flex", flexDirection: "column", gap: "16px", padding: "30px", maxHeight: '70vh', overflow: 'auto' }}>
                        <Box sx={{ display: "flex", gap: "16px" }}>
                            <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: "10px" }}>
                                <Typography sx={{ fontSize: "14px", fontWeight: "400", lineHeight: "22px" }}>
                                    Franchise Name *
                                </Typography>
                                <InputBase 
                                    value={formData.name} 
                                    name="name"
                                    onChange={handleInputChange}
                                    sx={{ 
                                        backgroundColor: "white", 
                                        width: "100%", 
                                        height: "32px", 
                                        borderRadius: "2px", 
                                        border: "1px solid", 
                                        borderColor: errors.name ? "red" : "secondary.border", 
                                        padding: "5px 12px", 
                                        fontSize: "14px", 
                                        fontWeight: "400", 
                                        lineHeight: "22px", 
                                        color: "black" 
                                    }} 
                                    placeholder="Enter franchise name" 
                                />
                                {errors.name && (
                                    <Typography style={{ color: "red", fontSize: "12px", fontWeight: "400", lineHeight: "22px" }}>
                                        {errors.name}
                                    </Typography>
                                )}
                            </Box>
                        </Box>

                        <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                            <Typography sx={{ fontSize: "14px", fontWeight: "400", lineHeight: "22px" }}>
                                Description *
                            </Typography>
                            <TextField
                                value={formData.description}
                                name="description"
                                onChange={handleInputChange}
                                multiline
                                rows={3}
                                placeholder="Enter franchise description"
                                error={!!errors.description}
                                helperText={errors.description}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        fontSize: "14px",
                                        fontWeight: "400",
                                        lineHeight: "22px"
                                    }
                                }}
                            />
                        </Box>

                        <Box sx={{ display: "flex", gap: "16px" }}>
                            <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: "10px" }}>
                                <Typography sx={{ fontSize: "14px", fontWeight: "400", lineHeight: "22px" }}>
                                    Contact Person *
                                </Typography>
                                <InputBase 
                                    value={formData.contact_person} 
                                    name="contact_person"
                                    onChange={handleInputChange}
                                    sx={{ 
                                        backgroundColor: "white", 
                                        width: "100%", 
                                        height: "32px", 
                                        borderRadius: "2px", 
                                        border: "1px solid", 
                                        borderColor: errors.contact_person ? "red" : "secondary.border", 
                                        padding: "5px 12px", 
                                        fontSize: "14px", 
                                        fontWeight: "400", 
                                        lineHeight: "22px", 
                                        color: "black" 
                                    }} 
                                    placeholder="Enter contact person name" 
                                />
                                {errors.contact_person && (
                                    <Typography style={{ color: "red", fontSize: "12px", fontWeight: "400", lineHeight: "22px" }}>
                                        {errors.contact_person}
                                    </Typography>
                                )}
                            </Box>
                            <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: "10px" }}>
                                <Typography sx={{ fontSize: "14px", fontWeight: "400", lineHeight: "22px" }}>
                                    Contact Email *
                                </Typography>
                                <InputBase 
                                    value={formData.contact_email} 
                                    name="contact_email"
                                    onChange={handleInputChange}
                                    sx={{ 
                                        backgroundColor: "white", 
                                        width: "100%", 
                                        height: "32px", 
                                        borderRadius: "2px", 
                                        border: "1px solid", 
                                        borderColor: errors.contact_email ? "red" : "secondary.border", 
                                        padding: "5px 12px", 
                                        fontSize: "14px", 
                                        fontWeight: "400", 
                                        lineHeight: "22px", 
                                        color: "black" 
                                    }} 
                                    placeholder="Enter contact email" 
                                />
                                {errors.contact_email && (
                                    <Typography style={{ color: "red", fontSize: "12px", fontWeight: "400", lineHeight: "22px" }}>
                                        {errors.contact_email}
                                    </Typography>
                                )}
                            </Box>
                        </Box>

                        <Box sx={{ display: "flex", gap: "16px" }}>
                            <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: "10px" }}>
                                <Typography sx={{ fontSize: "14px", fontWeight: "400", lineHeight: "22px" }}>
                                    Contact Phone *
                                </Typography>
                                <InputBase 
                                    value={formData.contact_phone} 
                                    name="contact_phone"
                                    onChange={handleInputChange}
                                    sx={{ 
                                        backgroundColor: "white", 
                                        width: "100%", 
                                        height: "32px", 
                                        borderRadius: "2px", 
                                        border: "1px solid", 
                                        borderColor: errors.contact_phone ? "red" : "secondary.border", 
                                        padding: "5px 12px", 
                                        fontSize: "14px", 
                                        fontWeight: "400", 
                                        lineHeight: "22px", 
                                        color: "black" 
                                    }} 
                                    placeholder="Enter contact phone" 
                                />
                                {errors.contact_phone && (
                                    <Typography style={{ color: "red", fontSize: "12px", fontWeight: "400", lineHeight: "22px" }}>
                                        {errors.contact_phone}
                                    </Typography>
                                )}
                            </Box>
                            <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: "10px", justifyContent: "center" }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                    <Switch
                                        checked={formData.is_active}
                                        onChange={handleInputChange}
                                        name="is_active"
                                    />
                                    <Typography sx={{ fontSize: "14px", fontWeight: "400", lineHeight: "22px" }}>
                                        Active
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>

                        <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                            <Typography sx={{ fontSize: "14px", fontWeight: "400", lineHeight: "22px" }}>
                                Address *
                            </Typography>
                            <TextField
                                value={formData.address}
                                name="address"
                                onChange={handleInputChange}
                                multiline
                                rows={2}
                                placeholder="Enter franchise address"
                                error={!!errors.address}
                                helperText={errors.address}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        fontSize: "14px",
                                        fontWeight: "400",
                                        lineHeight: "22px"
                                    }
                                }}
                            />
                        </Box>

                        {/* Logo Upload (style copied from product_details upload tiles) */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            <Typography sx={{ fontSize: '14px', fontWeight: '400', lineHeight: '22px' }}>
                                Franchise Logo
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
                                {logoPreview ? (
                                    <Box sx={{ width: '104px', height: '104px', border: '1px solid', borderColor: 'secondary.border', borderRadius: '8px', display: 'flex', justifyContent: 'center', alignItems: 'center', position: 'relative', backgroundColor: 'background.upload', overflow: 'hidden', '&:hover .overlay': { display: 'block' }, '&:hover .delete-button': { display: 'flex' } }}>
                                        <img src={logoPreview} alt="Franchise Logo" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '8px' }} />
                                        <Box className="overlay" sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: '#00000080', display: 'none', borderRadius: '8px' }} />
                                        <Tooltip title="Delete" arrow>
                                            <IconButton className="delete-button" onClick={handleRemoveLogo} sx={{ position: 'absolute', top: '50%', left: '50%', display: 'none', transform: 'translate(-50%, -50%)' }}>
                                                <DeleteIcon sx={{ color: 'white' }} />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                ) : null}

                                <Box sx={{ width: '104px', height: '104px', border: '1px solid', borderColor: 'secondary.border', borderRadius: '8px', display: 'flex', justifyContent: 'center', alignItems: 'center', position: 'relative', cursor: 'pointer', backgroundColor: 'background.upload' }}>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        id="franchise-logo-upload"
                                        style={{ position: 'absolute', width: '104px', height: '104px', opacity: 0, cursor: 'pointer', top: 0, left: 0 }}
                                        onChange={handleLogoFileChange}
                                        name="logo"
                                        disabled={uploadingLogo}
                                    />
                                    <label htmlFor="franchise-logo-upload" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                        <AddIcon fontSize="large" />
                                        <Typography sx={{ fontSize: '14px', fontWeight: '400', lineHeight: '22px' }}>{uploadingLogo ? 'Uploading…' : 'Upload'}</Typography>
                                    </label>
                                </Box>
                            </Box>
                        </Box>

                        {/* Color Fields */}
                        <Box sx={{ display: "flex", gap: "16px" }}>
                            <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: "10px" }}>
                                <Typography sx={{ fontSize: "14px", fontWeight: "400", lineHeight: "22px" }}>
                                    Background Color
                                </Typography>
                                <InputBase 
                                    value={formData.background_color || ''} 
                                    name="background_color"
                                    onChange={handleInputChange}
                                    sx={{ 
                                        backgroundColor: "white", 
                                        width: "100%", 
                                        height: "32px", 
                                        borderRadius: "2px", 
                                        border: "1px solid", 
                                        borderColor: "secondary.border", 
                                        padding: "5px 12px", 
                                        fontSize: "14px", 
                                        fontWeight: "400", 
                                        lineHeight: "22px", 
                                        color: "black" 
                                    }} 
                                    placeholder="#FFFFFF or css color" 
                                />
                            </Box>
                            <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: "10px" }}>
                                <Typography sx={{ fontSize: "14px", fontWeight: "400", lineHeight: "22px" }}>
                                    Content Color
                                </Typography>
                                <InputBase 
                                    value={formData.content_color || ''} 
                                    name="content_color"
                                    onChange={handleInputChange}
                                    sx={{ 
                                        backgroundColor: "white", 
                                        width: "100%", 
                                        height: "32px", 
                                        borderRadius: "2px", 
                                        border: "1px solid", 
                                        borderColor: "secondary.border", 
                                        padding: "5px 12px", 
                                        fontSize: "14px", 
                                        fontWeight: "400", 
                                        lineHeight: "22px", 
                                        color: "black" 
                                    }} 
                                    placeholder="#000000 or css color" 
                                />
                            </Box>
                            <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: "10px" }}>
                                <Typography sx={{ fontSize: "14px", fontWeight: "400", lineHeight: "22px" }}>
                                    Border Color
                                </Typography>
                                <InputBase 
                                    value={formData.border_color || ''} 
                                    name="border_color"
                                    onChange={handleInputChange}
                                    sx={{ 
                                        backgroundColor: "white", 
                                        width: "100%", 
                                        height: "32px", 
                                        borderRadius: "2px", 
                                        border: "1px solid", 
                                        borderColor: "secondary.border", 
                                        padding: "5px 12px", 
                                        fontSize: "14px", 
                                        fontWeight: "400", 
                                        lineHeight: "22px", 
                                        color: "black" 
                                    }} 
                                    placeholder="#E0E0E0 or css color" 
                                />
                            </Box>
                        </Box>

                        <Box sx={{ display: "flex", gap: "16px", justifyContent: "flex-end", marginTop: "16px" }}>
                            <Button 
                                variant="outlined" 
                                onClick={handleCloseModal}
                                sx={{ textTransform: "none" }}
                            >
                                Cancel
                            </Button>
                            <Button 
                                variant="contained" 
                                onClick={handleSubmit}
                                disabled={franchiseLoading}
                                sx={{ textTransform: "none" }}
                            >
                                {franchiseLoading ? "Saving..." : (franchiseEdit ? "Update" : "Create")}
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </Modal>
        </Box>
    );
}
